import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Maximize2, Minimize2, Plus, Scaling, X } from 'lucide-react';
import type { HomeWidget } from '@nw/contracts';
import { Dialog, DialogContent, cn } from '@nw/ui';
import { toast, useEnabledWebModules, usePreferences, useReducedMotionPreference, useUpdatePreferences, type WidgetDef, type WidgetSize } from '@nw/web-platform';
import { coreWidgets, DEFAULT_LAYOUT } from './core-widgets.js';
import { useHomeEdit } from './homeEdit.js';
import { Dial, useDoubleTap, type DialItem } from '../shell/Orbit.js';
import { usePinch } from '../shell/gestures.js';

const SIZE_NAMES: Record<WidgetSize, string> = {
  s: 'Small',
  m: 'Wide',
  l: 'Large',
  w: 'Full row',
};

function SortableWidget({
  item,
  def,
  editing,
  index,
  onResize,
  onGrow,
  onShrink,
  onRemove,
}: {
  item: HomeWidget;
  def: WidgetDef;
  editing: boolean;
  index: number;
  onResize: () => void;
  onGrow: () => void;
  onShrink: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  // Pointer drags start anywhere on the widget; keyboard moves go through the grip button.
  const { onKeyDown, ...pointerListeners } = listeners ?? {};
  const own = useRef<HTMLLIElement | null>(null);
  const ref = useCallback(
    (el: HTMLLIElement | null) => {
      own.current = el;
      setNodeRef(el);
    },
    [setNodeRef],
  );
  // Double-tap a widget for its own small ring; pinch it to grow or shrink.
  const [ring, setRing] = useState<{ x: number; y: number } | null>(null);
  const doubleTap = useDoubleTap((p) => setRing(p));
  const pinch = usePinch(onShrink, onGrow);
  const canGrow = def.sizes.indexOf(item.size) < def.sizes.length - 1;
  const canShrink = def.sizes.indexOf(item.size) > 0;
  const ringItems: DialItem[] = [
    ...(canGrow ? [{ id: 'grow', label: 'Bigger', icon: Maximize2, run: onGrow }] : []),
    ...(canShrink ? [{ id: 'shrink', label: 'Smaller', icon: Minimize2, run: onShrink }] : []),
    { id: 'remove', label: 'Remove', icon: X, run: onRemove },
  ];
  return (
    <li
      ref={ref}
      onPointerUp={(e) => {
        // The widget body, not a link or button inside it.
        if (e.target instanceof Element && e.target.closest('a, button, input, select, textarea')) return;
        doubleTap.onPointerUp({ ...e, target: e.currentTarget } as typeof e);
      }}
      onPointerDownCapture={pinch.onPointerDown}
      onPointerMoveCapture={pinch.onPointerMove}
      onPointerUpCapture={pinch.onPointerUp}
      onPointerCancelCapture={pinch.onPointerCancel}
      className={cn('nw-widget', `nw-widget-${item.size}`, editing && 'nw-widget-editing', isDragging && 'nw-widget-dragging')}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      aria-label={def.title}
      {...pointerListeners}
    >
      {/* Entry motion lives on an inner layer so it never fights the drag transform. */}
      <motion.div
        className="nw-widget-body"
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          delay: Math.min(index, 8) * 0.035,
          type: 'spring',
          stiffness: 260,
          damping: 28,
        }}
      >
        <def.Component size={item.size} />
      </motion.div>
      <div className="nw-widget-controls">
        <button
          type="button"
          className="nw-widget-btn nw-widget-grip"
          aria-label={`Move ${def.title}. Press space, use arrow keys, then space to drop.`}
          {...attributes}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement> | undefined}
        >
          <GripVertical className="size-4" />
        </button>
        {editing && def.sizes.length > 1 ? (
          <button type="button" className="nw-widget-btn" onClick={onResize} aria-label={`Resize ${def.title}, now ${SIZE_NAMES[item.size]}`}>
            <Scaling className="size-4" />
          </button>
        ) : null}
        {editing ? (
          <button type="button" className="nw-widget-btn" onClick={onRemove} aria-label={`Remove ${def.title}`}>
            <X className="size-4" />
          </button>
        ) : null}
      </div>
      {ring ? <Dial origin={ring} dragging={false} items={ringItems} onClose={() => setRing(null)} /> : null}
    </li>
  );
}

/** Homebase instruments: every module can offer widgets; you choose, size and order them. */
export function WidgetBoard() {
  const modules = useEnabledWebModules();
  const prefs = usePreferences();
  const update = useUpdatePreferences();
  const reduced = useReducedMotionPreference();
  const { editing, gallery, setEditing, setGallery } = useHomeEdit();
  // Leaving Homebase ends customizing.
  useEffect(() => () => setEditing(false), [setEditing]);
  const available = useMemo(() => [...coreWidgets, ...modules.flatMap((m) => m.widgets ?? [])], [modules]);
  const byId = useMemo(() => new Map(available.map((w) => [w.id, w])), [available]);

  const stored = prefs.data?.homeWidgets;
  const layout: HomeWidget[] = useMemo(() => {
    const base =
      stored ??
      DEFAULT_LAYOUT.map((id) => ({
        id,
        size: byId.get(id)?.defaultSize ?? 'm',
      }));
    // Widgets of turned-off modules simply disappear; the layout keeps their place for later.
    return base
      .filter((w) => byId.has(w.id))
      .map((w) => ({
        ...w,
        size: byId.get(w.id)!.sizes.includes(w.size) ? w.size : byId.get(w.id)!.defaultSize,
      }));
  }, [stored, byId]);

  const save = (next: HomeWidget[]) =>
    update.mutate(
      { homeWidgets: next },
      {
        onError: () => toast.error('Your layout could not be saved. Try again.'),
      },
    );

  // Mouse: drag after a few pixels, so clicks still open things. Touch: press and hold, so swipes still scroll.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 280, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = layout.findIndex((w) => w.id === e.active.id);
    const to = layout.findIndex((w) => w.id === e.over!.id);
    save(arrayMove(layout, from, to));
  };
  const unused = available.filter((w) => !layout.some((l) => l.id === w.id));

  return (
    <section aria-label="Instruments" className="nw-board">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={layout.map((w) => w.id)} strategy={rectSortingStrategy}>
          <ul className={cn('nw-board-grid', editing && !reduced && 'nw-board-jiggle')}>
            {layout.map((item, i) => (
              <SortableWidget
                key={item.id}
                index={i}
                item={item}
                def={byId.get(item.id)!}
                editing={editing}
                onResize={() => {
                  const sizes = byId.get(item.id)!.sizes;
                  const next = sizes[(sizes.indexOf(item.size) + 1) % sizes.length]!;
                  save(layout.map((w) => (w.id === item.id ? { ...w, size: next } : w)));
                }}
                onGrow={() => {
                  const sizes = byId.get(item.id)!.sizes;
                  const next = sizes[Math.min(sizes.length - 1, sizes.indexOf(item.size) + 1)]!;
                  if (next !== item.size) save(layout.map((w) => (w.id === item.id ? { ...w, size: next } : w)));
                }}
                onShrink={() => {
                  const sizes = byId.get(item.id)!.sizes;
                  const next = sizes[Math.max(0, sizes.indexOf(item.size) - 1)]!;
                  if (next !== item.size) save(layout.map((w) => (w.id === item.id ? { ...w, size: next } : w)));
                }}
                onRemove={() => save(layout.filter((w) => w.id !== item.id))}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {!layout.length ? <p className="mt-4 text-sm text-[var(--nw-quiet)]">No widgets yet. Choose Customize at the side, then Add widget.</p> : null}
      <Dialog open={gallery} onOpenChange={setGallery}>
        <DialogContent title="Add a widget" description="Each module offers live instruments for Homebase.">
          <ul className="grid gap-2">
            {unused.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  onClick={() => {
                    save([...layout, { id: w.id, size: w.defaultSize }]);
                    setGallery(false);
                  }}
                  className="flex w-full items-center justify-between gap-3 rounded-[16px] border border-[var(--nw-line)] p-4 text-start hover:border-[var(--nw-line-strong)]"
                >
                  <span>
                    <span className="block font-semibold">{w.title}</span>
                    <span className="block text-sm text-[var(--nw-quiet)]">{w.description}</span>
                  </span>
                  <Plus className="size-4 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </section>
  );
}
